import React, { useCallback, useEffect, useRef, useState } from "react"
import styled from "styled-components"
import Button from "./Button"
import { CURSOR_TYPES, LOCALES } from "@repo/utils/constants"
import { getFontSize } from "@repo/utils"
import { BREAKPOINTS } from "../../../styles/themeSettings"
import { useRouter } from "next/router"

async function fallbackCopyToClipboard(text) {
	return new Promise((resolve, reject) => {
		try {
			const textarea = document.createElement("textarea")
			textarea.value = text
			textarea.style.position = "fixed"
			textarea.style.top = "-1000px"
			textarea.style.left = "-1000px"
			document.body.appendChild(textarea)
			textarea.focus()
			textarea.select()
			const successful = document.execCommand("copy")
			document.body.removeChild(textarea)
			if (successful) resolve()
			else reject(new Error('execCommand("copy") returned false'))
		} catch (err) {
			reject(err)
		}
	})
}

const isSecureContext = typeof window !== "undefined" && window.isSecureContext

function canUseClipboardApi() {
	return typeof navigator !== "undefined" && navigator.clipboard && isSecureContext
}

const Wrapper = styled.div`
	position: relative;
	width: fit-content;
`

const Tooltip = styled.div`
	position: absolute;
	top: -8px;
	left: 50%;
	background-color: ${({ theme }) => theme?.colors?.white || "white"};
	pointer-events: none;
	font-size: ${getFontSize(14, BREAKPOINTS.MOBILE)};
	font-weight: ${({ theme }) => theme?.fontWeight?.semiBold || 500};
	z-index: 10;
	border-radius: 8px;
	padding: 10px 25px;
	border: 1px solid var(--secondary-color);
	transform: translateX(-50%) translateY(0) scale(0);
	opacity: 0;
	transition:
		transform 0.3s ease-in-out,
		opacity 0.2s ease-in-out;
	@media (min-width: ${({ theme }) => theme?.breakpoints?.tablet || 768}px) {
		font-size: ${getFontSize(16, BREAKPOINTS.MOBILE)};
	}
	@media (min-width: ${({ theme }) => theme?.breakpoints?.desktop || 1024}px) and (orientation: landscape) {
		font-size: ${getFontSize(18, BREAKPOINTS.DESKTOP)};
	}
	&.visible {
		opacity: 1;
		z-index: 10;
		transition:
			transform 0.2s ease-in-out,
			opacity 0.2s ease-in-out 0.05s;
		transform: translateX(-50%) translateY(-100%) scale(1);
	}
`

const COPY_TEXT_TRANSLATIONS = {
	[LOCALES.PL]: "Skopiowano!",
	[LOCALES.EN]: "Copied!",
}

const CopyButton = ({
	label = "",
	copiedLabel = "",
	value = "",
	className = "",
	cursor = CURSOR_TYPES.LINK || null,
	icon: Icon = undefined,
	children,
}) => {
	const [copied, setCopied] = useState(false)
	const hideTimerRef = useRef(null)
	const content = children ? children : label
	const { locale } = useRouter()
	const onClick = useCallback(async () => {
		if (!value) return
		try {
			const canCopy = canUseClipboardApi()
			if (canCopy) {
				await navigator.clipboard.writeText(value)
			} else {
				await fallbackCopyToClipboard(value)
			}
			setCopied(true)

			if (hideTimerRef.current) clearTimeout(hideTimerRef.current)

			hideTimerRef.current = setTimeout(() => {
				setCopied(false)
			}, 800)
		} catch (e) {
			console.log(e)
		}
	}, [value])

	useEffect(() => {
		return () => {
			if (hideTimerRef.current) clearTimeout(hideTimerRef.current)
		}
	}, [])
	return (
		<Wrapper onClick={onClick} className={`copy-button ${className}`}>
			<Tooltip className={copied ? "visible" : ""}>{copiedLabel || COPY_TEXT_TRANSLATIONS[locale]}</Tooltip>
			<Button
				as="div"
				className="arrow underline-animation"
				variant="unstyled"
				data-cursor={cursor}
				data-copied={copied ? "true" : "false"}
			>
				{content}
			</Button>
			{Icon}
		</Wrapper>
	)
}

export default CopyButton
